import React, { Component, useState, useEffect, useRef, useCallback } from 'react';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import GoogleMapReact from 'google-map-react';
import { featureCollectionToTurfPolygons } from './helpers/utils/createTurfPolygons';
import Heading from '../heading';
import Overlay from './helpers/ui/overlay';
import withFetch from '../../hoc/withFetch';
import withAuthProvider from '../../hoc/withAuthProvider';
import SpinnerDots from '../spinner/dots';
import InactiveDistricts from './inactive-districts';
import ActiveDistricts from './active-districts';
import { ButtonAnimation } from '../export-btn';
import DropdownButton from '../dropdown-button';
import ExportFooter from './export-footer'; 
import { generateFeatures } from './helpers/utils/convert-to-geojson';
import LocationHandler from './helpers/ui/location-handler';
import Classifications from './classifications/ClassificationsPage';
import { getColoredPinCssString } from './helpers/utils/pin-color-utils';
import { GOOGLE_MAPS_API_KEY, REACT_APP_API_BASE} from '../../config';
import useDistricts from './hooks/useDistricts';
import './mydistricts.scss';

class Map extends Component {
    map = null;
    maps = null;
    salesDistrictsOverlay = null;
    salesDistricts = null;

    constructor(props) {
        super(props);

        this.state = {
            pins: null
        };
    }

    async fetchPins() {        
        const res = await this.props.fetch(`${REACT_APP_API_BASE}/salesdistrict/units/${this.props.type}`);
        return await res.json();
    }

    async fetchSpatialInfo() {
        const { match } = this.props;
        const res = await this.props.fetch(`${REACT_APP_API_BASE}/salesdistrict/spatialinfo/${this.props.type}${match.params.scenarioId ? `/${match.params.scenarioId}` : ''}`);
        return await res.json();
    }

    async renderSaleDistricts(visible = true) {
        const districts = await this.fetchSpatialInfo();
        
        if (districts && districts.length > 0) {
            const features = [];
            districts.forEach((district, i) => {
                if (district && district.geoJson) {
                    const parsedDistrict = JSON.parse(district.geoJson);
                    
                    // Säkerhetskontroll: kontrollera att parsedDistrict[0] och properties finns
                    if (parsedDistrict && parsedDistrict[0] && parsedDistrict[0].properties) {
                        parsedDistrict[0].properties.homeMarkerGeoJson = district.homeMarkerGeoJson 
                            ? JSON.parse(district.homeMarkerGeoJson) 
                            : null;
                        
                        // Använd id om det finns, annars använd name som fallback
                        const featureId = parsedDistrict[0].properties.id || `${parsedDistrict[0].properties.name}${i}`;
                        const generatedFeature = generateFeatures(featureId, parsedDistrict[0], 'salesdistrict');
                        
                        // Filtrera bort null-värden (generateFeatures kan returnera null om feature saknar geometry)
                        if (generatedFeature) {
                            features.push(generatedFeature);
                        }
                    } else {
                        console.warn(`District ${i} has invalid geoJson structure:`, parsedDistrict);
                    }
                }
            });

            this.salesDistricts = {
                type: "FeatureCollection",
                features: features.filter(f => f !== null) // Ytterligare säkerhet: filtrera bort null-värden
            };
    
            this.map.data.addGeoJson(this.salesDistricts);
    
            this.salesDistricts.features.forEach(district => {
                const feature = this.map.data.getFeatureById(district.id);
                if (feature) {
                    this.map.data.overrideStyle(feature, { 
                        strokeWeight: 0.5, 
                        strokeColor: feature.getProperty('color'),
                        fillOpacity: 0.5,
                        fillColor: feature.getProperty('color'),
                        clickable: false,
                        visible,
                        zIndex:1
                    });
                }
            });
    
            this.createLabelOverlay();
        }        

        if (this.props.onLoad) {
            this.props.onLoad();   
        }        
    }

    handleApiLoaded(map, maps) {
        this.map = map;
        this.maps = maps;
        this.props.onApiLoaded(map, maps);
        this.renderSaleDistricts(map, maps);

        this.maps.event.addListener(this.map, 'zoom_changed', () => {
            this.removeLabelsForSalesDistricts();
        });

        this.maps.event.addListener(this.map, 'idle', () => {
            this.removeLabelsForSalesDistricts();
            this.createLabelOverlay();
        });
    }

    createLabelOverlay() {
        if (!this.salesDistricts) return;

        this.salesDistrictsOverlay = Overlay({ 
            polygons: featureCollectionToTurfPolygons(this.salesDistricts), 
            map: this.map, 
            maps: this.maps,
            ignoreArea: true,
            modifiers: {
                'overlay-label--districts': true
            }
        });
    }

    removeLabelsForSalesDistricts() {
        if (this.salesDistrictsOverlay) {
            this.salesDistrictsOverlay.setMap(null);
        }
    }

    async toggleUnits() {
        if (this.props.onBeforeLoad) {
            this.props.onBeforeLoad();
        }

        let pins = this.state.pins;
        if (!pins) {
            pins = await this.fetchPins();
        }

        // Ta bort alla markers först
        LocationHandler.removeMarkers();

        // Samla alla units från alla distrikt där showPins är true
        const allUnitsWithPins = [];
        const districtsWithPins = [];

        // Skapa ett objekt för snabb lookup av distrikt baserat på namn
        const districtsByName = {};
        this.props.activeDistricts.forEach((d, i) => {
            districtsByName[d.name] = { district: d, index: i };
        });

        // Loop genom alla features på kartan och matcha med activeDistricts baserat på namn
        this.map.data.forEach((feature) => {
            const featureType = feature.getProperty('type');
            const featureName = feature.getProperty('name');
            
            // Bara hantera salesdistrict features
            if (featureType !== 'salesdistrict' || !featureName) {
                return;
            }

            const districtInfo = districtsByName[featureName];
            if (!districtInfo) {
                return;
            }

            const d = districtInfo.district;
            const i = districtInfo.index;
            
            if (d.showPins) {
                // Dölj distriktets bakgrundsfärg när pins visas
                this.map.data.overrideStyle(feature, { 
                    visible: false
                });
        
                const pinsInDistrict = pins.filter(f => d.units.indexOf(f.companyId) > -1);
                
                // Lägg till distrikt-info till varje unit så vi kan använda rätt färg
                pinsInDistrict.forEach(unit => {
                    allUnitsWithPins.push({
                        ...unit,
                        districtColor: d.color,
                        districtIndex: i
                    });
                });
                
                districtsWithPins.push({ district: d, index: i });
            } else {
                // Visa distriktets bakgrundsfärg igen när pins döljs
                this.map.data.overrideStyle(feature, { 
                    visible: true,
                    strokeWeight: 0.5,
                    strokeColor: d.color,
                    fillOpacity: 0.5,
                    fillColor: d.color,
                    clickable: false,
                    zIndex: 1
                });
            }
        });

        // Skapa alla markers i ett anrop med rätt färg för varje unit
        if (allUnitsWithPins.length > 0) {
            // Gruppera units per distrikt för att använda rätt färg
            const unitsByDistrict = {};
            allUnitsWithPins.forEach(unit => {
                const key = unit.districtIndex;
                if (!unitsByDistrict[key]) {
                    unitsByDistrict[key] = [];
                }
                unitsByDistrict[key].push(unit);
            });

            // Skapa markers för varje distrikt med rätt färg
            for (const [districtIndex, units] of Object.entries(unitsByDistrict)) {
                const districtInfo = districtsWithPins.find(d => d.index === parseInt(districtIndex));
                if (districtInfo) {
                    const pinCss = getColoredPinCssString(districtInfo.district.color);
                    
                    const markers = units.map(unit => {
                        if (!unit.latitude || !unit.longitude) return null;
                        
                        return LocationHandler.createMarker(
                            this.map,
                            this.maps,
                            new this.maps.LatLng(unit.latitude, unit.longitude),
                            async ({ units }) => {},
                            unit,
                            pinCss
                        );
                    }).filter(Boolean);

                    markers.forEach(marker => {
                        marker.setMap(this.map);
                        LocationHandler.markers.push(marker);
                    });
                }
            }
            
            LocationHandler.fitBounds(this.map, this.maps);
        }
      
        this.setState({
           pins
        });
        
        if (this.props.onLoad) {
            this.props.onLoad();   
        } 

        return;
    } 

    componentDidUpdate(prevProps) {
        if (!prevProps.activeDistricts || !this.props.activeDistricts) {
            return;
        }
        
        // Kolla om något distrikt ändrade sin showPins-status
        // Matcha distrikt baserat på ID istället för index (för att hantera sortering)
        const hasChanged = prevProps.activeDistricts.some((prevDistrict) => {
            const currentDistrict = this.props.activeDistricts.find(d => d.id === prevDistrict.id);
            return currentDistrict && prevDistrict.showPins !== currentDistrict.showPins;
        });
        
        if (hasChanged) {
            this.toggleUnits();
        }
    }

    render() {

        const center = {
            lat: 63.8536388, lng: 15.5452901
        };

        return (
            <GoogleMapReact
                bootstrapURLKeys={{
                    key: GOOGLE_MAPS_API_KEY,                                         
                    language: 'sv' 
                }}
                defaultZoom={5}
                defaultCenter={center}
                yesIWantToUseGoogleMapApiInternals={true}
                onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}>
            </GoogleMapReact>
        );
    }
}

const MyDistricts = (props) => {
    const mapRef = useRef(null);
    const mapsRef = useRef(null);
    const dropDownRef = useRef(null);
    
    const [mapIsLoading, setMapIsLoading] = useState(true);
    const [sortOrderSavePending, setSortOrderSavePending] = useState(false);
    const [editModalIsOpen, setEditModalIsOpen] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isExportingFinished, setIsExportingFinished] = useState(false);
    
    const scenarioId = props.match?.params?.scenarioId || null;
    const { activeDistricts, inActiveDistricts, scenarios, isLoading: dataIsLoading, reload: fetchDistricts } = useDistricts(
        props.type,
        scenarioId,
        props.fetch
    );

    useEffect(() => {
        const handleAfterPrint = () => {
            setIsPrinting(false);
        };
        
        window.addEventListener('afterprint', handleAfterPrint);
        
        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    const total = useCallback((key) => {
        if (!activeDistricts) return null;
        return activeDistricts.reduce((a, b) => a + b[key], 0);
    }, [activeDistricts]);

    const toggleEditModal = useCallback(() => {
        setEditModalIsOpen(prev => !prev);
    }, []);

    const getMimeType = useCallback((format) => {
        if (format === 'excel') {
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        }
        return 'application/xml';
    }, []);

    const exportDistricts = useCallback(async (format) => {
        setIsExporting(true);

        if (dropDownRef.current) {
            dropDownRef.current.hideDropDown();
        }

        const res = await props.fetch(`${REACT_APP_API_BASE}/salesdistrict/exportall/${format}/${props.type}${scenarioId ? `/${scenarioId}` : ''}`);
        
        if (res.ok) {
            const exportReport = await res.blob();
            var blob = new Blob([exportReport], { type: getMimeType(format) });
            var link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = format === 'xml' ? `Distrikt.xml` : `Distrikt.xlsx`;
            link.click();

            setIsExporting(false);
            setIsExportingFinished(true);

            setTimeout(() => {
                setIsExportingFinished(false);
            }, 4000);
        }
    }, [props, scenarioId, getMimeType]);

    const getExportStateText = useCallback(() => {
        if (isExporting) {
            return 'Exporterar';
        }
        if (isExportingFinished) {
            return 'Klar!';
        }
        return 'Exportera';
    }, [isExporting, isExportingFinished]);

    const getFoodServicePreSlug = useCallback(() => {
        if (scenarioId) {
            return `/foodservice-scenario/${scenarioId}`;
        }
        return `/foodservice-scenario`;
    }, [scenarioId]);

    const getLivsPreSlug = useCallback(() => {
        if (scenarioId) {
            return `/scenario/${scenarioId}`;
        }
        return `/scenario`;
    }, [scenarioId]);
    const preSlug = props.type === 'foodservice' ? getFoodServicePreSlug() : getLivsPreSlug();
    
    // Hantera toggle pins - behöver uppdatera activeDistricts lokalt
    const [localActiveDistricts, setLocalActiveDistricts] = useState(null);
    
    useEffect(() => {
        if (activeDistricts) {
            setLocalActiveDistricts(activeDistricts);
        }
    }, [activeDistricts]);

    // Wrapper för fetchDistricts som uppdaterar localActiveDistricts efter reload
    const handleFetchDistricts = useCallback(async (sortKey = null, sortDirection = null) => {
        await fetchDistricts(sortKey, sortDirection);
        // activeDistricts kommer att uppdateras via hooken, vilket triggar useEffect ovan
    }, [fetchDistricts]);

    const handleTogglePins = useCallback((district, checked) => {
        setLocalActiveDistricts(prev => {
            if (!prev) return prev;
            return prev.map(d => ({
                ...d,
                showPins: district.id === d.id ? checked : d.showPins
            }));
        });
    }, []);

    const handleToggleAllPins = useCallback(() => {
        setLocalActiveDistricts(prev => {
            if (!prev) return prev;
            const checked = !(prev.filter(f => f.showPins === true).length === prev.length);
            return prev.map(d => ({
                ...d,
                showPins: checked
            }));
        });
    }, []);

    const districtsToUse = localActiveDistricts || activeDistricts;
    const activeDistrictsTableLoading = dataIsLoading || sortOrderSavePending;

    return (
        <div className={classNames({
            'districts__loader': true,
            'districts__loader--is-loading': dataIsLoading || mapIsLoading
        })}>
            <Heading heading={props.heading} modifiers={{
                'page-header--wrap': true
            }}>
                {((activeDistricts && activeDistricts.length > 0) || (inActiveDistricts && inActiveDistricts.length > 0) || (scenarios && scenarios.length > 1)) &&
                    <button onClick={() => props.toggleScenario(!props.isScenarioOpen)} className="districts__add-scenario" type="button">+ Nytt/Ändra scenario</button>
                }
                {(scenarios && scenarios.length > 1) && 
                    <div className="districts__scenario-tabs-container">
                        <ul className="districts__scenario-tabs">
                            {scenarios.map((item, i) => {
                                return (
                                    <li key={item.salesDistrictScenarioId}>
                                        <a href={`${props.type === 'foodservice' ? '/foodservice-scenario/' : '/scenario/'}${item.salesDistrictScenarioId}`} className={classNames({
                                            'districts__scenario-tabs-item--selected': (item.salesDistrictScenarioId.toString() === scenarioId) || (!scenarioId && i === 0)
                                        })}>{item.name}</a>
                                    </li>
                                ); 
                            })}
                        </ul>
                    </div>
                }
            </Heading>
            {scenarios &&
                <>
                    <div className={classNames({
                        'districts__map-test': true,
                        'districts__map-test--is-loading': mapIsLoading
                    })}>
                        <div className={classNames({
                            'districts__map': true,
                            'districts__map--is-loading': mapIsLoading,
                            'districts__map--is-printing': isPrinting
                        })}>
                            <Map 
                                {...props} 
                                onApiLoaded={(map, maps) => {
                                    mapRef.current = map;
                                    mapsRef.current = maps;
                                }}
                                activeDistricts={districtsToUse} 
                                onBeforeLoad={() => {
                                    setMapIsLoading(true);
                                }}
                                onLoad={() => {
                                    setMapIsLoading(false);
                                }} />
                        </div>
                    </div>

                    <ActiveDistricts 
                        {...props} 
                        activeDistricts={districtsToUse}
                        inActiveDistricts={inActiveDistricts}
                        scenarios={scenarios}
                        dataIsLoading={activeDistrictsTableLoading}
                        mapIsLoading={mapIsLoading}
                        editModalIsOpen={editModalIsOpen}
                        isPrinting={isPrinting}
                        isCopying={isCopying}
                        isExporting={isExporting}
                        isExportingFinished={isExportingFinished}
                        setDataIsLoading={setSortOrderSavePending}
                        fetchDistricts={handleFetchDistricts}
                        total={total}
                        preSlug={preSlug}
                        handleTogglePins={handleTogglePins}
                        handleToggleAllPins={handleToggleAllPins} />

                    <InactiveDistricts districts={inActiveDistricts} type={props.type} />
                    
                    {props.type === 'livs' &&
                        <Classifications />
                    }

                    {activeDistricts && activeDistricts.length > 0 &&
                        <ExportFooter 
                            print={() => {
                                setMapIsLoading(true);
                                setIsPrinting(true);
                                setTimeout(() => {
                                    setMapIsLoading(false);
                                    window.print();
                                }, 1500);
                            }} 
                            activeDistricts={activeDistricts}
                            inActiveDistricts={inActiveDistricts}
                            scenarios={scenarios}
                            mapIsLoading={mapIsLoading}
                            dataIsLoading={dataIsLoading}
                            editModalIsOpen={editModalIsOpen}
                            isPrinting={isPrinting}
                            isCopying={isCopying}
                            isExporting={isExporting}
                            isExportingFinished={isExportingFinished}
                            btnComponent={<DropdownButton 
                                ref={dropDownRef} 
                                controls="export-dropdown" 
                                buttonModifiers={{
                                    'export-footer__button': true
                                }}
                                buttonChildren={<><div className="export-footer__download-icon"><ButtonAnimation/></div>{getExportStateText()}</>}
                                text="Exportera"
                                modifiers={{
                                    'export-footer__dropdown-button': true
                                }}>
                                <li>
                                    <button type="button" onClick={() => exportDistricts('excel')}>Excel</button>    
                                </li>
                                <li>
                                    <button type="button" onClick={() => exportDistricts('xml')}>XML</button>
                                </li>
                            </DropdownButton>}></ExportFooter>
                    }
                </>
            }
            <SpinnerDots />
        </div>
    );
};

export default withAuthProvider(withFetch(withRouter(MyDistricts)));